/**
 * Ventana modal.
 *
 * Detalle de diseño que marca la diferencia: en CELULAR se comporta como una
 * "hoja" que sube desde abajo (como las apps nativas), y en PANTALLA GRANDE como
 * un diálogo centrado. Es el mismo componente; solo cambian las clases.
 *
 * Un modal centrado y pequeño en un celular deja el teclado tapando la mitad del
 * formulario. La hoja inferior evita ese problema.
 */

import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { Boton, cx } from "./ui";
import { bloquearDesplazamiento } from "@/lib/bloquearDesplazamiento";

/**
 * Las ventanas abiertas, de la de abajo a la de arriba.
 *
 * ⚠️ Escape cierra SOLO la de arriba. Antes cada ventana escuchaba la tecla por
 * su cuenta, y con una encima de otra —la ficha de un producto nuevo abierta
 * desde la Entrada de mercancía— un Escape cerraba las dos: se perdía la
 * entrada a medio cargar por querer cerrar la ficha.
 */
const pilaDeVentanas: object[] = [];

export function Modal({
  abierto,
  onCerrar,
  titulo,
  descripcion,
  children,
  pie,
  ancho = "md",
}: {
  abierto: boolean;
  onCerrar: () => void;
  titulo: string;
  descripcion?: string;
  children: ReactNode;
  /** Botones de acción. Van fijos abajo para que siempre se alcancen. */
  pie?: ReactNode;
  ancho?: "xs" | "sm" | "md" | "lg";
}) {
  // `onCerrar` en una referencia: casi siempre llega como función nueva en
  // cada pintado, y si el efecto de abajo dependiera de ella, la ventana de
  // DEBAJO se volvería a apuntar en la pila al escribir en ella —quedando
  // «arriba» sin estarlo— y Escape cerraría la equivocada.
  const cerrar = useRef(onCerrar);
  cerrar.current = onCerrar;

  // Cerrar con la tecla Escape y bloquear el desplazamiento del fondo.
  useEffect(() => {
    if (!abierto) return;

    const yo = {};
    pilaDeVentanas.push(yo);

    const alPulsarTecla = (e: KeyboardEvent) => {
      if (e.key === "Escape" && pilaDeVentanas[pilaDeVentanas.length - 1] === yo) {
        cerrar.current();
      }
    };
    document.addEventListener("keydown", alPulsarTecla);

    // Contado con las demás: ver `bloquearDesplazamiento` (la página que no
    // bajaba con la rueda del mouse).
    const soltar = bloquearDesplazamiento();

    return () => {
      document.removeEventListener("keydown", alPulsarTecla);
      const i = pilaDeVentanas.indexOf(yo);
      if (i >= 0) pilaDeVentanas.splice(i, 1);
      soltar();
    };
  }, [abierto]);

  if (!abierto) return null;

  /**
   * El ancho crece con la pantalla.
   *
   * Antes se quedaba clavado en el tamaño de tableta: en un monitor de 27
   * pulgadas el modal de cobro ocupaba un tercio de la pantalla y dejaba dos
   * franjas negras enormes a los lados, mientras los botones de cobro rápido
   * —lo que el cajero mira— salían apretados en dos columnas estrechas.
   *
   * ═══════════════════════════════════════════════════════════════════════
   *  ⚠️ Y AUN ASÍ SE QUEDABAN PEQUEÑOS, PORQUE CASI TODOS USABAN `sm`
   * ═══════════════════════════════════════════════════════════════════════
   *
   * Lo dijo el dueño de un formulario concreto —«esa es muy pequeña»— pero al
   * contarlos eran más de treinta pantallas: crear un método de pago, dar de
   * alta un proveedor, configurar una moneda, registrar una entrada. Todas
   * formularios de verdad, todas metidas en 448 píxeles, todas obligando a
   * bajar y subir para ver lo que se acababa de escribir.
   *
   * `sm` era estrecho porque lo compartía con los cuadros de «¿seguro que
   * quieres borrar?», que ahí sí tienen que ser pequeños: una pregunta, dos
   * botones, nada más. Al separarlos —`xs` para preguntar, `sm` para
   * trabajar— cada uno puede tener el tamaño que le toca.
   *
   * ═══════════════════════════════════════════════════════════════════════
   *  ⚠️ Y EN UNA PANTALLA DE 1080 SEGUÍAN APRETADAS (2026-09-25)
   * ═══════════════════════════════════════════════════════════════════════
   *
   * El editor de métodos de pago, en `md`, se quedaba en 768 píxeles en el
   * centro de una pantalla de 1920, y había que bajar y subir. El dueño:
   * «hazla cómodamente ancha según la pantalla», y después, de todas: «que no
   * pase más». Por eso:
   *
   *   · `sm` y `md` crecen un paso más en pantallas grandes (`2xl`, desde
   *     1536 píxeles: los monitores de 1080 y los portátiles grandes).
   *
   *   · El contenido es un `@container`: puede medir el ancho REAL de la
   *     ventana y no el de la pantalla, que con la barra lateral y los
   *     márgenes no dicen lo mismo.
   *
   *   · ⚠️ UN FORMULARIO LARGO VA EN `lg`, y en secciones que ponen el título
   *     a un lado y los campos al otro cuando hay hueco: `SeccionDeFormulario`
   *     o `SeccionNumerada`. Solo ensanchar la ventana no basta: una lista de
   *     mil píxeles con una palabra dentro está igual de mal que una apretada.
   */
  const anchos = {
    // Una pregunta y dos botones. Ancho no le hace ningún bien: la vista se
    // va a los bordes en vez de al texto que dice qué se va a borrar.
    xs: "sm:max-w-md",
    sm: "sm:max-w-lg lg:max-w-xl 2xl:max-w-2xl",
    md: "sm:max-w-2xl lg:max-w-3xl 2xl:max-w-4xl",
    lg: "sm:max-w-3xl lg:max-w-5xl xl:max-w-6xl",
  };

  /*
    ⚠️ SE DIBUJA EN EL <body>, NO DONDE SE ESCRIBE (2026-10-19).

    Una ventana abierta desde la columna de «La cuenta» de la Caja nacía dentro
    de un `sticky`, y un `sticky` encierra a sus hijos en su propia capa: el
    `z-50` de la ventana solo valía dentro de esa columna. El menú lateral
    (`z-30`, pero en la capa de fuera) quedaba ENCIMA: sin oscurecer, se podía
    tocar con la ventana abierta y, en pantallas de 1024, le tapaba un borde.
    Desde el <body> ninguna columna, tarjeta ni contenedor puede encerrarla.
    Los eventos de React siguen subiendo por donde se escribió.
  */
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label={titulo}
    >
      {/* Fondo oscuro: al tocarlo se cierra */}
      <div
        className="animar-fondo absolute inset-0 bg-black/50 backdrop-blur-[2px]"
        onClick={onCerrar}
        aria-hidden
      />

      <div
        className={cx(
          "relative flex max-h-[92dvh] w-full flex-col bg-surface shadow-[var(--shadow-pop)]",
          // Celular: hoja inferior con esquinas superiores redondeadas.
          "rounded-t-3xl",
          // Pantalla grande: caja centrada.
          "sm:rounded-[var(--radius-modal)]",
          anchos[ancho],
          "animar-entrada",
        )}
      >
        {/* Encabezado */}
        <div className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-ink">{titulo}</h2>
            {descripcion && <p className="mt-0.5 text-xs text-muted">{descripcion}</p>}
          </div>
          <button
            type="button"
            onClick={onCerrar}
            aria-label="Cerrar"
            className="-mr-1 -mt-1 shrink-0 rounded-lg p-2 text-muted transition-colors hover:bg-surface-hover hover:text-ink"
          >
            <X size={18} />
          </button>
        </div>

        {/*
          Contenido: es lo único que se desplaza. Y es un `@container`, para que
          lo de dentro se acomode al ancho de la ventana (ver `anchos`).
        */}
        <div className="scroll-fino @container min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>

        {/*
          Pie fijo con los botones.

          ═══════════════════════════════════════════════════════════════════
           ⚠️ TIENE FONDO PROPIO, Y NO ES ADORNO
          ═══════════════════════════════════════════════════════════════════

          Antes era del mismo color que el contenido, separado solo por una
          línea de `border-line` — que en tema claro es casi blanca sobre
          blanco. El contenido se desplaza POR DEBAJO de esta barra, así que el
          campo que quedaba a medias se veía cortado a ras de los botones, sin
          nada que dijera dónde termina una cosa y empieza la otra.

          El dueño lo describió como «esos botones me aparecen cortados», y lo
          que estaba cortado era el formulario detrás. Con el pie en otro tono
          se lee como lo que es: una barra fija, y lo de arriba sigue.
        */}
        {pie && (
          <div className="flex flex-col-reverse gap-2 border-t border-line-strong bg-surface-2 px-5 py-4 pb-safe shadow-[0_-6px_14px_-8px_rgb(0_0_0/0.18)] sm:flex-row sm:justify-end">
            {pie}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

/**
 * Confirmación para acciones delicadas (desactivar un producto, quitar a alguien
 * del equipo).
 *
 * Se usa en vez de `window.confirm()` porque el diálogo del navegador se ve
 * ajeno a la aplicación y en celular a veces ni aparece.
 */
export function Confirmar({
  abierto,
  onCerrar,
  onConfirmar,
  titulo,
  mensaje,
  textoConfirmar = "Confirmar",
  peligroso = false,
  cargando = false,
}: {
  abierto: boolean;
  onCerrar: () => void;
  onConfirmar: () => void;
  titulo: string;
  /**
   * Texto o JSX. Si es texto, los saltos de línea se respetan: casi todos
   * estos mensajes son «qué pasa» + línea en blanco + «qué NO pasa», y esa
   * segunda parte es la que hace que la persona se atreva a tocar el botón.
   */
  mensaje: ReactNode;
  textoConfirmar?: string;
  peligroso?: boolean;
  cargando?: boolean;
}) {
  return (
    <Modal
      abierto={abierto}
      onCerrar={onCerrar}
      titulo={titulo}
      ancho="xs"
      pie={
        <>
          <Boton variante="secundario" onClick={onCerrar} disabled={cargando}>
            Cancelar
          </Boton>
          <Boton
            variante={peligroso ? "peligro" : "primario"}
            onClick={onConfirmar}
            cargando={cargando}
          >
            {textoConfirmar}
          </Boton>
        </>
      }
    >
      <p className="whitespace-pre-line text-sm leading-relaxed text-ink-soft">{mensaje}</p>
    </Modal>
  );
}

/**
 * Pedir el motivo de algo delicado (anular una cuenta, quitar un plato que la
 * cocina ya hizo).
 *
 * Reemplaza a `window.prompt()`, que además de verse ajeno tiene un problema
 * peor: en varios navegadores de celular NO APARECE. La persona toca «Anular»,
 * no pasa nada visible, y concluye que el botón está roto.
 *
 * ⚠️ Los motivos sugeridos no son decoración.
 *
 * Un recuadro en blanco que pregunta «¿por qué?» es una prueba sorpresa: quien
 * no escribe rápido pone «x» o «ya» con tal de salir, y el registro queda
 * inservible justo donde más falta hace. Con tres botones que dicen los
 * motivos de siempre, se toca uno y ya está — y el que sea de verdad distinto
 * escribe lo suyo.
 */
export function PedirMotivo({
  abierto,
  onCerrar,
  onConfirmar,
  titulo,
  mensaje,
  sugerencias = [],
  textoConfirmar = "Confirmar",
  cargando = false,
}: {
  abierto: boolean;
  onCerrar: () => void;
  onConfirmar: (motivo: string) => void;
  titulo: string;
  mensaje?: ReactNode;
  sugerencias?: string[];
  textoConfirmar?: string;
  cargando?: boolean;
}) {
  const [motivo, setMotivo] = useState("");

  // Al abrirlo se limpia: el motivo de la vez pasada no es el de esta.
  useEffect(() => {
    if (abierto) setMotivo("");
  }, [abierto]);

  const sirve = motivo.trim().length >= 3;

  return (
    <Modal
      abierto={abierto}
      onCerrar={onCerrar}
      titulo={titulo}
      ancho="xs"
      pie={
        <>
          <Boton variante="secundario" onClick={onCerrar} disabled={cargando}>
            Cancelar
          </Boton>
          <Boton
            variante="peligro"
            onClick={() => onConfirmar(motivo.trim())}
            disabled={!sirve}
            cargando={cargando}
          >
            {textoConfirmar}
          </Boton>
        </>
      }
    >
      {mensaje && (
        <p className="whitespace-pre-line text-sm leading-relaxed text-ink-soft">{mensaje}</p>
      )}

      {sugerencias.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {sugerencias.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setMotivo(s)}
              className={cx(
                "rounded-full border px-3 py-1.5 text-xs transition-colors",
                motivo === s
                  ? "border-accent bg-accent-soft font-medium text-accent"
                  : "border-line text-ink-soft hover:bg-surface-hover",
              )}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      <textarea
        value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
        rows={2}
        autoFocus
        placeholder={sugerencias.length > 0 ? "O escribe otro motivo…" : "Escribe el motivo…"}
        className="mt-3 w-full rounded-[var(--radius-control)] border border-line bg-surface px-3 py-2 text-sm text-ink outline-none placeholder:text-faint focus:border-accent"
      />

      {/* Solo se avisa si ya empezó a escribir: regañar a quien no ha tocado nada sobra. */}
      {motivo.length > 0 && !sirve && (
        <p className="mt-1.5 text-xs text-warning">Escribe al menos tres letras.</p>
      )}
    </Modal>
  );
}
